"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthenticatedApiRequest = void 0;
var aws_amplify_1 = require("aws-amplify");
/** Returns an object describing a data structure, or simply the type if the data is a simple type */
var describe = function (data) {
    var described = {};
    if (['number', 'undefined', 'boolean', 'function', 'string'].indexOf(typeof data) !== -1) {
        return typeof data;
    }
    else if (data === null) {
        return 'null';
    }
    Object.keys(data).forEach(function (key) {
        switch (true) {
            case ['number', 'undefined', 'boolean', 'function', 'string'].indexOf(typeof data[key]) !== -1:
                described[key] = typeof data[key];
                break;
            case data[key] === null:
                described[key] = 'null';
                break;
            case Array.isArray(data[key]):
                if (data[key][0] !== undefined) {
                    var recursion = describe(data[key][0]);
                    described[key] = { Array: recursion };
                }
                else {
                    described[key] = "empty Array";
                }
                break;
            case typeof data[key] === 'object':
                var proto = Object.prototype.toString.call(data[key]);
                if (proto === '[object Object]' || proto === '[object Array]') {
                    described[key] = describe(data[key]);
                }
                else {
                    described[key] = Object.prototype.toString
                        .call(data[key])
                        .slice(8, -1);
                }
                break;
            default:
                described[key] = 'type not detected';
        }
    });
    return described;
};
/**
 * Function for calling API Gateway with a Cognito user token.
 * @see {@link AuthApiError} for error format
 * @example
 * ```
 * import { AuthenticatedApiRequest } from "gsf-util-react-auth";
 *
 *  const getTest = (): Promise<string> => {
 *      return new Promise((resolve, reject) => {
 *        AuthenticatedApiRequest({
 *          method: 'get',
 *          apiName: 'GatewayGoldSpotTest',
 *          path: '/test'
 *        })
 *        .then((result: GatewayResponse) => {
 *          // any manipulation that is specific to this request
 *          if (result['claims']) {
 *            resolve(result['claims']['cognito:username']);
 *          } else {
 *            throw new Error('No claims attached');
 *          }
 *        })
 *        .catch((err) => {
 *          reject(err);
 *        });
 *      });
 *    };
 * ```
 */
var AuthenticatedApiRequest = function (config) {
    var data = config.postData || {};
    var error = function (err) {
        return {
            error: err,
            origin: 'AuthAPI',
            data: {
                apiName: config.apiName,
                path: config.path,
                postData: describe(config.postData),
            },
        };
    };
    switch (config.method) {
        case 'get':
            return new Promise(function (resolve, reject) {
                aws_amplify_1.API.get(config.apiName, config.path, {})
                    .then(function (res) {
                    resolve(res);
                })
                    .catch(function (err) {
                    var _a, _b, _c;
                    if (((_a = err.response) === null || _a === void 0 ? void 0 : _a.status) === 401 &&
                        ((_c = (_b = err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) === 'Unauthorized') {
                        aws_amplify_1.Auth.signOut()
                            .then(function () {
                            reject(error(err));
                        })
                            .catch(function () {
                            reject(error(err));
                        });
                    }
                    else {
                        reject(error(err));
                    }
                });
            });
        case 'post':
            return new Promise(function (resolve, reject) {
                aws_amplify_1.API.post(config.apiName, config.path, data)
                    .then(function (res) {
                    resolve(res);
                })
                    .catch(function (err) {
                    var _a, _b, _c;
                    if (((_a = err.response) === null || _a === void 0 ? void 0 : _a.status) === 401 &&
                        ((_c = (_b = err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) === 'Unauthorized') {
                        aws_amplify_1.Auth.signOut()
                            .then(function () {
                            reject(error(err));
                        })
                            .catch(function () {
                            reject(error(err));
                        });
                    }
                    else {
                        reject(error(err));
                    }
                });
            });
        case 'put':
            return new Promise(function (resolve, reject) {
                aws_amplify_1.API.put(config.apiName, config.path, data)
                    .then(function (res) {
                    resolve(res);
                })
                    .catch(function (err) {
                    var _a, _b, _c;
                    if (((_a = err.response) === null || _a === void 0 ? void 0 : _a.status) === 401 &&
                        ((_c = (_b = err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) === 'Unauthorized') {
                        aws_amplify_1.Auth.signOut()
                            .then(function () {
                            reject(error(err));
                        })
                            .catch(function () {
                            reject(error(err));
                        });
                    }
                    else {
                        reject(error(err));
                    }
                });
            });
        case 'delete':
            return new Promise(function (resolve, reject) {
                aws_amplify_1.API.del(config.apiName, config.path, data)
                    .then(function (res) {
                    resolve(res);
                })
                    .catch(function (err) {
                    var _a, _b, _c;
                    if (((_a = err.response) === null || _a === void 0 ? void 0 : _a.status) === 401 &&
                        ((_c = (_b = err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message) === 'Unauthorized') {
                        aws_amplify_1.Auth.signOut()
                            .then(function () {
                            reject(error(err));
                        })
                            .catch(function () {
                            reject(error(err));
                        });
                    }
                    else {
                        reject(error(err));
                    }
                });
            });
        default:
            var newconfig = Object.assign({}, config, { method: 'get' });
            if (config.hasOwnProperty('body')) {
                Object.assign(newconfig, { method: 'post' });
            }
            return (0, exports.AuthenticatedApiRequest)(newconfig);
    }
};
exports.AuthenticatedApiRequest = AuthenticatedApiRequest;
